import { create } from "zustand";
import type { ChatMessageView, ChatStreamEvent, ChatThreadDTO } from "@shared/types";
import { t } from "@/i18n";
import { get, readSSE } from "@/lib/api";
import { useUI } from "@/lib/store";
import { tripApi } from "@/lib/trip";

interface ChatState {
  tripId: string | null;
  threadId: string | null;
  threads: ChatThreadDTO[];
  messages: ChatMessageView[];
  live: ChatMessageView | null;
  streaming: boolean;
  draft: string;
  loading: boolean;
  init: (tripId: string) => Promise<void>;
  openThread: (id: string | null) => Promise<void>;
  setDraft: (v: string) => void;
  prefill: (text: string) => void;
  send: (text: string, opts?: { tripId?: string; fromWidget?: { widgetId: string; title: string; payload: unknown; label?: string | null; threadId?: string | null }; context?: string | null }) => Promise<void>;
  undo: (batchId: string) => Promise<void>;
  stop: () => void;
}

let ctrl: AbortController | null = null;

const LAST_THREAD = (tripId: string) => `tb:thread:${tripId}`;

export const useChat = create<ChatState>((set, getState) => ({
  tripId: null,
  threadId: null,
  threads: [],
  messages: [],
  live: null,
  streaming: false,
  draft: "",
  loading: false,

  init: async (tripId) => {
    if (getState().tripId === tripId) return;
    set({ tripId, threadId: null, messages: [], threads: [], live: null });
    try {
      const threads = await get<ChatThreadDTO[]>(`/api/trips/${tripId}/chat/threads`);
      set({ threads });
      let last: string | null = null;
      try {
        last = localStorage.getItem(LAST_THREAD(tripId));
      } catch {
        /* ignore */
      }
      const pick = threads.find((th) => th.id === last) ?? threads[0];
      if (pick) await getState().openThread(pick.id);
    } catch {
      /* офлайн или нет прав */
    }
  },

  openThread: async (id) => {
    const tripId = getState().tripId;
    if (!tripId) return;
    try {
      if (id) localStorage.setItem(LAST_THREAD(tripId), id);
      else localStorage.removeItem(LAST_THREAD(tripId));
    } catch {
      /* ignore */
    }
    if (!id) return set({ threadId: null, messages: [], live: null });
    set({ loading: true, threadId: id });
    try {
      const messages = await get<ChatMessageView[]>(`/api/trips/${tripId}/chat/threads/${id}`);
      if (getState().threadId === id) set({ messages });
    } finally {
      set({ loading: false });
    }
  },

  setDraft: (v) => set({ draft: v }),
  prefill: (text) => {
    set({ draft: text });
    useUI.getState().set({ chatOpen: true, mobileView: "chat" });
  },

  send: async (text, opts) => {
    if (opts?.tripId && opts.tripId !== getState().tripId) await getState().init(opts.tripId);
    const st = getState();
    const tripId = st.tripId;
    if (!tripId || st.streaming) return;
    const fw = opts?.fromWidget;
    let threadId = st.threadId;
    if (fw?.threadId && st.threads.some((th) => th.id === fw.threadId)) {
      if (fw.threadId !== threadId) await getState().openThread(fw.threadId);
      threadId = fw.threadId;
    }
    const now = new Date().toISOString();
    const userMsg: ChatMessageView = {
      id: `u-${Date.now()}`,
      role: "user",
      text,
      createdAt: now,
      fromWidget: fw ? { widgetId: fw.widgetId, title: fw.title } : null,
    };
    const live: ChatMessageView = { id: `a-${Date.now()}`, role: "assistant", text: "", thinking: "", tools: [], widgetIds: [], createdAt: now };
    set({ messages: [...getState().messages, userMsg], live, streaming: true, draft: "" });
    ctrl = new AbortController();
    const patch = (p: (m: ChatMessageView) => ChatMessageView) => {
      const cur = getState().live;
      if (cur) set({ live: p(cur) });
    };
    try {
      await readSSE(
        `/api/trips/${tripId}/chat`,
        {
          method: "POST",
          json: {
            threadId,
            text,
            context: opts?.context ?? null,
            fromWidget: fw ? { widgetId: fw.widgetId, title: fw.title, payload: fw.payload, label: fw.label ?? null } : null,
          },
        },
        (event, data) => {
          if (event !== "message") return;
          const e = data as ChatStreamEvent;
          switch (e.type) {
            case "start":
              if (!getState().threads.some((th) => th.id === e.threadId)) {
                const title = (fw ? t("Виджет: {title}", { title: fw.title }) : text).slice(0, 70);
                set({ threads: [{ id: e.threadId, tripId, userId: "", title, createdAt: now, updatedAt: now }, ...getState().threads] });
              }
              set({ threadId: e.threadId });
              try {
                localStorage.setItem(LAST_THREAD(tripId), e.threadId);
              } catch {
                /* ignore */
              }
              break;
            case "text":
              patch((m) => ({ ...m, text: m.text + e.delta }));
              break;
            case "thinking":
              patch((m) => ({ ...m, thinking: (m.thinking ?? "") + e.delta }));
              break;
            case "tool_start":
              patch((m) => ({ ...m, tools: [...(m.tools ?? []), { id: e.id, name: e.name, label: e.label }] }));
              break;
            case "tool_end":
              patch((m) => ({ ...m, tools: (m.tools ?? []).map((tool) => (tool.id === e.id ? { ...tool, ok: e.ok, summary: e.summary } : tool)) }));
              break;
            case "server_tool":
              patch((m) => ({ ...m, tools: [...(m.tools ?? []), { id: `s-${Date.now()}`, name: e.name, label: e.label, ok: true }] }));
              break;
            case "map_focus": {
              const ui = useUI.getState();
              if (e.day) ui.selectDay(e.day);
              if (e.bounds) ui.focusMap({ bounds: e.bounds, placeIds: e.placeIds });
              else if (e.center) ui.focusMap({ center: e.center, zoom: e.zoom });
              if (window.innerWidth < 1024) ui.set({ mobileView: "map" });
              break;
            }
            case "widget":
              patch((m) => ({ ...m, widgetIds: [...new Set([...(m.widgetIds ?? []), e.widgetId])] }));
              break;
            case "done":
              patch((m) => ({ ...m, batchId: e.batchId, changes: e.changes }));
              break;
            case "error":
              patch((m) => ({ ...m, error: e.message }));
              break;
          }
        },
        ctrl.signal,
      );
    } catch (e) {
      if ((e as Error).name !== "AbortError") patch((m) => ({ ...m, error: (e as Error).message }));
    } finally {
      const final = getState().live;
      set({ messages: final ? [...getState().messages, final] : getState().messages, live: null, streaming: false });
      ctrl = null;
    }
  },

  undo: async (batchId) => {
    const tripId = getState().tripId;
    if (!tripId) return;
    const r = await tripApi.undo(tripId, { batchId });
    set({ messages: getState().messages.map((m) => (m.batchId === batchId ? { ...m, undone: true } : m)) });
    useUI.getState().toast(r.conflicts.length ? t("Отменено, но часть полей уже изменили другие ({n})", { n: r.conflicts.length }) : t("Отменено изменений: {n}", { n: r.undone }));
  },

  stop: () => {
    // сервер всё равно доведёт ход до конца (история должна остаться целой); просто перестаём слушать
    ctrl?.abort();
  },
}));
